import React, { useCallback, useEffect, useState } from 'react';
import { Activity, Layers, Server } from 'lucide-react';
import { executionOperationsService } from '../../services/executionOperationsService';

export const OperationsDashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<Record<string, unknown> | null>(null);
  const [status, setStatus] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dash, st] = await Promise.all([
        executionOperationsService.getDashboard(),
        executionOperationsService.getStatus(),
      ]);
      setDashboard((dash.data as Record<string, unknown>) || null);
      setStatus((st.data as Record<string, unknown>) || null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 30000);
    return () => window.clearInterval(id);
  }, [load]);

  const summary = (dashboard?.summary as Record<string, number>) || {};

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard icon={<Server className="h-5 w-5" />} label="Live workers" value={summary.live_workers ?? '—'} />
        <StatCard icon={<Layers className="h-5 w-5" />} label="Queues" value={summary.live_queues ?? '—'} />
        <StatCard icon={<Activity className="h-5 w-5" />} label="Queue depth" value={summary.total_queue_depth ?? '—'} />
      </div>
      {status && (
        <div className="rounded-xl border border-enterprise-200 bg-white p-4 text-sm">
          <h3 className="font-semibold text-enterprise-900">Phase 3 compatibility</h3>
          <pre className="mt-2 overflow-auto rounded bg-enterprise-50 p-3 text-xs text-enterprise-700">
            {JSON.stringify(status, null, 2)}
          </pre>
        </div>
      )}
      {!loading && dashboard && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="Top queues by depth">
            <ul className="divide-y divide-enterprise-100 text-sm">
              {((dashboard.top_queues_by_depth as Record<string, unknown>[]) || []).map((q) => (
                <li key={String(q.physical_queue)} className="flex justify-between py-2">
                  <span>{String(q.display_name || q.physical_queue)}</span>
                  <span className="font-mono text-enterprise-700">{String(q.depth ?? 0)}</span>
                </li>
              ))}
            </ul>
          </Panel>
          <Panel title="Workers (sample)">
            <ul className="divide-y divide-enterprise-100 text-sm">
              {((dashboard.workers as Record<string, unknown>[]) || []).slice(0, 8).map((w) => (
                <li key={String(w.worker_key)} className="py-2">
                  <div className="font-medium">{String(w.worker_key)}</div>
                  <div className="text-xs text-enterprise-500">{String(w.state)} · {String(w.worker_class)}</div>
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: unknown }> = ({ icon, label, value }) => (
  <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
    <div className="flex items-center gap-2 text-enterprise-500">{icon}<span className="text-xs uppercase tracking-wide">{label}</span></div>
    <div className="mt-2 text-2xl font-bold text-enterprise-900">{String(value)}</div>
  </div>
);

const Panel: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-xl border border-enterprise-200 bg-white p-4 shadow-sm">
    <h3 className="mb-3 font-semibold text-enterprise-900">{title}</h3>
    {children}
  </div>
);
